import * as React from 'react';
import { IntlShape } from 'react-intl';
import GoogleMapsService from '../../Services/GoogleMapsService';
import PositionTooltip from './PositionTooltip';
import {
  EVENT_ACTIVE_LINE_COLOR,
  IS_EVENT,
  IS_START_POSITION,
  IS_STOP_POSITION,
} from './constants';
import {
  IAnnouncement,
  IRide,
  IRideDetail,
  IRideMapMarkerTooltips,
  IRideMapMarkers,
  IRidePosition,
  TToggleTooltip,
  TTooltip,
} from './types';
import { getEventsFromAnnouncementsInPosition } from './RidePlayerService';
import { positionMarkerIcon } from './RidePlayerIconService';

export interface IRideMapEventPositionMarkerTooltips {
  [index: number]: google.maps.OverlayView[];
}

export interface IRideMapEventPositionMarkers {
  [rideNumber: number]: google.maps.Marker[];
}

export interface IRideMapCombinedEventMarkers {
  [rideNumber: number]: {
    [positionIndex: number]: {
      ids: number[];
      marker: google.maps.Marker;
    };
  };
}

/**
 * Create event position marker
 * @param {google.maps.Map} map
 * @param {google.maps.LatLng} position
 * @returns {google.maps.Marker}
 */
export const createEventPositionMarker = (
  map: google.maps.Map | undefined,
  position: google.maps.LatLng,
  infoWindow?: TTooltip
): google.maps.Marker => {
  const icon: google.maps.Icon = {
    url: positionMarkerIcon(EVENT_ACTIVE_LINE_COLOR),
    anchor: new google.maps.Point(6, 6),
  };
  const markerOptions: google.maps.MarkerOptions = {
    icon,
    position,
    map,
    zIndex: 98,
  };
  const marker = new google.maps.Marker(markerOptions);
  marker.set('originalColor', EVENT_ACTIVE_LINE_COLOR);
  if (infoWindow) {
    marker.addListener('mouseover', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
    marker.addListener('mouseout', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
  }
  return marker;
};

/**
 * Create event position markers
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {TToggleTooltip} handleToggleInfoWindow
 * @returns {IRideMapEventPositionMarkers}
 */
export const createEventPositionMarkers = (
  map: google.maps.Map | undefined,
  rides: IRide[],
  handleToggleInfoWindow: TToggleTooltip
): IRideMapEventPositionMarkers => {
  const ecoDrivePositionMarkers: IRideMapEventPositionMarkers = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    ecoDrivePositionMarkers[rideNumber] = [];
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      if (
        ride.positions[positionIndex].type !== IS_START_POSITION ||
        ride.positions[positionIndex].type !== IS_STOP_POSITION
      ) {
        const ecoDrivePositionMarker = createEventPositionMarker(
          map,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleToggleInfoWindow }
        );
        ecoDrivePositionMarkers[rideNumber].push(ecoDrivePositionMarker);
      }
    }
  }
  return ecoDrivePositionMarkers;
};

/**
 * Create ecoDrive position marker tooltips
 * @param {(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void} handleCloseInfoWindow
 * @returns {IRideMapMarkerInfoWindows}
 */
export const createEventPositionMarkerTooltips = (
  rides: IRide[],
  intl: IntlShape
): IRideMapEventPositionMarkerTooltips => {
  const positionTooltips: IRideMapEventPositionMarkerTooltips = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    positionTooltips[rideNumber] = [];
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      const positionTooltip = (
        <PositionTooltip
          intl={intl}
          position={ride.positions[positionIndex]}
          positionIndex={positionIndex}
        />
      );
      const positionTooltipOverlay = GoogleMapsService.createRidePositionMarkerOverlay(
        ride.positions[positionIndex],
        positionTooltip
      );

      positionTooltips[rideNumber].push(positionTooltipOverlay);
    }
  }
  return positionTooltips;
};

/**
 * Update event position markers with tooltip when new position is received
 * @param {google.maps.Map} map
 * @param {IRideMapMarkers} positionMarkers
 * @param {IRideMapMarkerTooltips} positionMarkerTooltips
 * @param {number} rideNumber
 * @param {IRideDetail} rideData
 * @param {IRidePosition} newPosition
 * @param {boolean} speedlimitsEnabled
 * @param {IRidePlayerSpeedLimitsSettings} speedlimitSettings
 * @param {number} positionIndex
 * @param {IntlShape} intl
 * @param {TToggleTooltip} handleToggleTooltip
 * @returns {void}
 */
export const updateEventPositionMarkersWithNewPosition = (
  map: google.maps.Map | undefined,
  positionMarkers: IRideMapMarkers,
  positionMarkerTooltips: IRideMapMarkerTooltips,
  rideNumber: number,
  rideData: IRideDetail,
  newPosition: IRidePosition,
  positionIndex: number,
  intl: IntlShape,
  handleToggleTooltip?: TToggleTooltip
): void => {
  const position = new google.maps.LatLng(newPosition.gpsLat, newPosition.gpsLon);
  const marker = createEventPositionMarker(
    map,
    position,
    handleToggleTooltip
      ? {
          rideNumber,
          positionIndex,
          toggleTooltipCallback: handleToggleTooltip,
        }
      : undefined
  );
  if (positionMarkers[rideNumber]) {
    positionMarkers[rideNumber].push(marker);
  }
  let positionTooltip: JSX.Element | null = null;
  positionTooltip = (
    <PositionTooltip intl={intl} position={newPosition} positionIndex={positionIndex} />
  );

  const positionMarkerTooltip = GoogleMapsService.createEventPositionMarkerOverlay(
    newPosition,
    positionTooltip
  );

  positionMarkerTooltips[rideNumber][positionIndex] = positionMarkerTooltip;
};

/**
 * Create event icon marker
 * @param {google.maps.Map} map
 * @param {google.maps.LatLng} position
 * @returns {google.maps.Marker}
 */
export const createEventIconMarker = (
  map: google.maps.Map | undefined,
  position: google.maps.LatLng,
  infoWindow?: TTooltip
): google.maps.Marker => {
  const icon: google.maps.Icon = {
    url: '/assets/images/player/EventMarker.svg',
    anchor: new google.maps.Point(18, 18),
  };
  const markerOptions: google.maps.MarkerOptions = {
    icon,
    position,
    map,
    zIndex: 99,
  };
  const marker = new google.maps.Marker(markerOptions);
  if (infoWindow) {
    marker.addListener('mouseover', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
    marker.addListener('mouseout', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
  }
  return marker;
};

/**
 * Create combined event icon markers
 * @param {google.maps.Map|undefined} map
 * @param {IRide[]} rides
 * @param {TToggleTooltip} handleTooltip
 * @returns {IRideMapCombinedEventMarkers}
 */
export const createCombinedEventIconMarkers = (
  map: google.maps.Map | undefined,
  rides: IRide[],
  handleTooltip: TToggleTooltip,
  activeRide: number
): IRideMapCombinedEventMarkers => {
  const eventMarkers: IRideMapCombinedEventMarkers = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    eventMarkers[rideNumber] = {};
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      const prevPosition = ride.positions[positionIndex - 1];
      const currentPosition = ride.positions[positionIndex];
      let prevPositionEvents: IAnnouncement[] = [];

      const currentPositionEvents = currentPosition.announcements.filter(
        (announcement: IAnnouncement) => announcement.type === IS_EVENT
      );
      if (prevPosition) {
        prevPositionEvents = prevPosition.announcements.filter(
          (announcement: IAnnouncement) => announcement.type === IS_EVENT
        );
      }
      // If previous position not exists and current position has events, create event marker
      if (!prevPosition && currentPositionEvents.length > 0) {
        // console.log('2. prevposition NOT exists and current position HAS events: ', positionIndex);
        const eventMarker = createEventIconMarker(
          rideNumber === activeRide ? map : undefined,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
        );
        let ids: number[] = [];
        ids = currentPositionEvents.map((event: IAnnouncement) => event.id);
        eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
      }
      // If previous position exists and has no events and current position has events
      if (prevPosition && prevPositionEvents.length === 0 && currentPositionEvents.length > 0) {
        // console.log('4. prevPosition exists and has NO events and current position HAS events: ', positionIndex);
        const eventMarker = createEventIconMarker(
          rideNumber === activeRide ? map : undefined,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
        );
        let ids: number[] = [];
        ids = currentPositionEvents.map((event: IAnnouncement) => event.id);
        eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
      }

      // If previous postion exist and has events and current position events
      if (prevPosition && prevPositionEvents.length > 0 && currentPositionEvents.length > 0) {
        // If event marker not exist for prev position
        if (!eventMarkers[rideNumber][positionIndex - 1]) {
          const prevEventIds = prevPositionEvents.map((event: IAnnouncement) => event.id);
          const currentPositionEventIds = currentPositionEvents.map(
            (event: IAnnouncement) => event.id
          );
          if (JSON.stringify(prevEventIds) === JSON.stringify(currentPositionEventIds)) {
            // console.log(
            //   '6.2.1 prev position events are EQUAL with current position events',
            //   positionIndex
            // );
          } else {
            // console.log(
            //   '6.2.2 prev position events are NOT EQUAL with current position events',
            //   positionIndex
            // );
            const currentPositionEventIdsWihtoutPreviousEventIds = currentPositionEventIds.filter(
              (id: number) => !prevEventIds.includes(id)
            );
            if (currentPositionEventIdsWihtoutPreviousEventIds.length >= 0) {
              const eventMarker = createEventIconMarker(
                rideNumber === activeRide ? map : undefined,
                new google.maps.LatLng(
                  ride.positions[positionIndex].gpsLat,
                  ride.positions[positionIndex].gpsLon
                ),
                { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
              );
              let ids: number[] = [];
              ids = currentPositionEvents.map((event: IAnnouncement) => event.id);
              eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
            }
          }
        }
      }
    }
  }
  return eventMarkers;
};

export const updateEventIconMarkersWithNewPosition = (
  map: google.maps.Map | undefined,
  eventMarkers: IRideMapCombinedEventMarkers,
  ride: IRide,
  rideNumber: number,
  newPosition: IRidePosition,
  positionIndex: number,
  handleTooltip: TToggleTooltip
): void => {
  const prevPosition = ride.positions[positionIndex - 1];
  const currentPosition = newPosition;
  const eventsForPosition = getEventsFromAnnouncementsInPosition(currentPosition);
  const eventsForPrevPosition = getEventsFromAnnouncementsInPosition(prevPosition);

  // if no occurence of event exists, create marker
  if (
    !prevPosition &&
    !eventMarkers[rideNumber][positionIndex] &&
    currentPosition &&
    eventsForPosition &&
    eventsForPosition.length > 0
  ) {
    const eventMarker = createEventIconMarker(
      map,
      new google.maps.LatLng(
        ride.positions[positionIndex].gpsLat,
        ride.positions[positionIndex].gpsLon
      ),
      { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
    );

    const ids: number[] = [];
    eventsForPosition.forEach((event: IAnnouncement) => {
      if (!ids.includes(event.id)) {
        ids.push(event.id);
      }
    });
    eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
  }

  // If event on previous position exists and is differrent, create marker
  if (
    prevPosition &&
    !eventMarkers[rideNumber][positionIndex] &&
    eventMarkers[rideNumber][positionIndex - 1] &&
    currentPosition &&
    eventsForPosition &&
    eventsForPosition.length > 0
  ) {
    const ids: number[] = [];
    eventsForPosition.forEach((event: IAnnouncement) => {
      if (!ids.includes(event.id)) {
        ids.push(event.id);
      }
    });
    if (JSON.stringify(ids) !== JSON.stringify(eventMarkers[rideNumber][positionIndex - 1].ids)) {
      const eventMarker = createEventIconMarker(
        map,
        new google.maps.LatLng(newPosition.gpsLat, newPosition.gpsLon),
        { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
      );

      eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
    }
  }

  // If event on previous position not exists and on current position exists - create marker
  if (
    prevPosition &&
    eventsForPrevPosition &&
    eventsForPrevPosition.length === 0 &&
    !eventMarkers[rideNumber][positionIndex] &&
    !eventMarkers[rideNumber][positionIndex - 1] &&
    currentPosition &&
    eventsForPosition &&
    eventsForPosition.length > 0
  ) {
    const ids: number[] = [];
    eventsForPosition.forEach((event: IAnnouncement) => {
      if (!ids.includes(event.id)) {
        ids.push(event.id);
      }
    });
    const eventMarker = createEventIconMarker(
      map,
      new google.maps.LatLng(currentPosition.gpsLat, currentPosition.gpsLon),
      { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
    );

    eventMarkers[rideNumber][positionIndex] = { ids, marker: eventMarker };
  }
};
